import * as yup from 'yup';

/**
 * Thought Testing (E01) — the per-event-type payload codecs of the batch transport (plan 03 §2.4).
 * Authored ONCE here, run on BOTH ends: the mobile outbox validates at append time (a malformed
 * step fails at the screen, never sits in the outbox), the server runs the same schema as pipeline
 * step (1). Re-exported through `@cc/domain` so the client imports one module for payload typing
 * and validation. Board examples are the fixtures (slice E01 · RecordActivatingEvent).
 */

/**
 * ADDITIVE ONLY, one entry per built slice, in the wizard's own step order. A type is never removed
 * or renamed: an old build's outbox may still be holding a proposal of any type this list has ever
 * carried, and the enum is what decides whether the server will take it.
 */
export const THOUGHT_TEST_EVENT_TYPES = [
  'ActivatingEventRecorded', // E01 · RecordActivatingEvent   (buildOrder 1)
  'BehaviorsRecorded', // E01 · RecordBehaviors               (buildOrder 4)
  'AutomaticThoughtRecorded', // E01 · RecordAutomaticThought (buildOrder 5)
  'EvidenceForRecorded', // E01 · RecordEvidenceFor           (buildOrder 6)
  'EvidenceAgainstRecorded', // E01 · RecordEvidenceAgainst   (buildOrder 7)
  'CognitiveDistortionsIdentified', // E01 · IdentifyCognitiveDistortions (buildOrder 8)
  'AlternativeThoughtRecorded', // E01 · RecordAlternativeThought (buildOrder 9)
  'OutcomeRated', // E01 · RateOutcome — COMPLETES the test   (buildOrder 10)
  'LocationReminderSet', // E01 · SetLocationReminder          (buildOrder 12)
] as const;
export type ThoughtTestEventTypeName =
  (typeof THOUGHT_TEST_EVENT_TYPES)[number];

const emotionSchema = yup.object({
  id: yup.number().integer().required(),
  strength: yup.number().integer().min(0).max(100).required(),
});

/**
 * Every wizard step shares three fields, so they are declared once. `subject` is re-checked against
 * the caller in the resolver — a codec says the shape is right, never that the write is allowed.
 * `legacyActivatingEventId` is SERVER-RESOLVED (T114 (d)) and stripped from anything a client sends:
 * a client that could name the legacy row it writes into could point a step at somebody else's card.
 */
const stepBase = {
  subject: yup.string().required(),
  performer: yup.string().optional(), // ATTR-1 — present only on on-behalf writes
  thoughtTestId: yup.string().required(),
};

/**
 * THE SITUATION (shape (b), founder ruling 2026-09-04: N thought tests share one activating event).
 *
 * Unlike `legacyActivatingEventId` this one IS client-minted and must travel on the wire: the wizard
 * mints `situations/<uuid7>` on its first step and carries it through the run, which is how the N
 * thought tests of one run come to name the same situation. It is optional because a standalone
 * quick-add card has none — that absence is the discriminator the whole strangler line runs along.
 *
 * It is added ONLY to the three payloads whose events carry it. `yup` does not strip unknown keys
 * here, and `toIncoming` hands the validated payload straight to `Event.make`, so putting it on
 * `stepBase` would quietly widen five other event types with a field their model never drew.
 */
const situationHandle = { activatingEventId: yup.string().optional() };

/** ActivatingEventRecorded payload — description non-empty (min 2, as the legacy activating event). */
export const activatingEventRecordedPayloadSchema = yup.object({
  subject: yup.string().required(),
  performer: yup.string().optional(), // ATTR-1 — present only on on-behalf writes
  thoughtTestId: yup.string().required(),
  ...situationHandle,
  description: yup.string().trim().required().min(2),
  emotions: yup.array().of(emotionSchema.required()).required(),
  feelings: yup.array().of(yup.number().integer().required()).required(),
  bodyPartsFeelings: yup.number().integer().min(0).required(),
});

export type ActivatingEventRecordedPayload = yup.Asserts<
  typeof activatingEventRecordedPayloadSchema
>;

/** Board type `BasicThought` (thought.gql): a thought plus the 0–100 belief in it. */
const basicThoughtSchema = yup.object({
  thought: yup.string().required(),
  thoughtBelief: yup.number().integer().min(0).max(100).required(),
});

/**
 * BehaviorsRecorded — OPTIONAL and backfill-safe by founder ruling, so `''` is a legitimate value:
 * "I looked at this step and had nothing to add" is a recorded fact, not a rejection. `.defined()`
 * rather than `.required()`, because yup's `required` rejects the empty string.
 */
export const behaviorsRecordedPayloadSchema = yup.object({
  ...stepBase,
  ...situationHandle, // `behaviors` is a column of the SHARED activatingEvents document
  behaviors: yup.string().defined(),
});

/** AutomaticThoughtRecorded — `cardName` is `''` until the wizard names the card (server default). */
export const automaticThoughtRecordedPayloadSchema = yup.object({
  ...stepBase,
  ...situationHandle, // the card's situation — what `DeleteActivatingEvent`'s guard reads
  automaticThought: yup.string().trim().required(),
  thoughtBelief: yup.number().integer().min(0).max(100).required(),
  cardName: yup.string().defined(),
});

/**
 * EvidenceForRecorded — the two arrays are POSITIONAL PARTNERS in the legacy shape
 * (`thoughtsFor[i]` ↔ `thoughtsForAlternativeThoughts[i]`), and the alternative slot is legitimately
 * null, so the codec keeps the pairing by asserting equal length rather than splitting them.
 */
export const evidenceForRecordedPayloadSchema = yup.object({
  ...stepBase,
  thoughtsFor: yup.array().of(yup.string().required()).required(),
  thoughtsForAlternativeThoughts: yup
    .array()
    .of(yup.string().nullable().defined())
    .required()
    .test(
      'pairs-with-thoughtsFor',
      'thoughtsForAlternativeThoughts must have one entry per thoughtsFor entry',
      function (value) {
        const partner = (this.parent as { thoughtsFor?: unknown[] })
          ?.thoughtsFor;
        return (
          !Array.isArray(partner) ||
          !Array.isArray(value) ||
          partner.length === value.length
        );
      },
    ),
});

export const evidenceAgainstRecordedPayloadSchema = yup.object({
  ...stepBase,
  thoughtsAgainst: yup.array().of(yup.string().required()).required(),
});

/**
 * CognitiveDistortionsIdentified — OPTIONAL by founder ruling: an EMPTY selection is the fact
 * "I looked and found none", so an empty array is valid and must not be rejected.
 */
export const cognitiveDistortionsIdentifiedPayloadSchema = yup.object({
  ...stepBase,
  cognitiveDistortions: yup.array().of(yup.string().required()).required(),
});

export const alternativeThoughtRecordedPayloadSchema = yup.object({
  ...stepBase,
  realisticThoughts: yup.array().of(basicThoughtSchema.required()).required(),
});

/**
 * OutcomeRated — COMPLETES the test. `initialNegativeEmotionStrength` is spelled correctly here
 * (D-fhir); the legacy row keeps its misspelling and the back-projection translates.
 */
export const outcomeRatedPayloadSchema = yup.object({
  ...stepBase,
  result: yup
    .object({
      initialThoughtBelief: yup.number().integer().min(0).max(100).required(),
      initialNegativeEmotionStrength: yup
        .number()
        .integer()
        .min(0)
        .max(100)
        .required(),
    })
    .required(),
});

/**
 * LocationReminderSet — `legacyThoughtId` is SERVER-RESOLVED like the other legacy bindings
 * (T114 (d)) and is therefore NOT accepted from a client: naming the legacy row you write into
 * would let a caller point a step at somebody else's card.
 */
export const locationReminderSetPayloadSchema = yup.object({
  ...stepBase,
  locationId: yup.string().required(),
  type: yup.number().integer().required(),
  enter: yup.boolean().optional(),
  leave: yup.boolean().optional(),
});

export type BehaviorsRecordedPayload = yup.Asserts<
  typeof behaviorsRecordedPayloadSchema
>;
export type AutomaticThoughtRecordedPayload = yup.Asserts<
  typeof automaticThoughtRecordedPayloadSchema
>;
export type EvidenceForRecordedPayload = yup.Asserts<
  typeof evidenceForRecordedPayloadSchema
>;
export type EvidenceAgainstRecordedPayload = yup.Asserts<
  typeof evidenceAgainstRecordedPayloadSchema
>;
export type CognitiveDistortionsIdentifiedPayload = yup.Asserts<
  typeof cognitiveDistortionsIdentifiedPayloadSchema
>;
export type AlternativeThoughtRecordedPayload = yup.Asserts<
  typeof alternativeThoughtRecordedPayloadSchema
>;
export type OutcomeRatedPayload = yup.Asserts<typeof outcomeRatedPayloadSchema>;
export type LocationReminderSetPayload = yup.Asserts<
  typeof locationReminderSetPayloadSchema
>;

const payloadSchemaByType: Record<ThoughtTestEventTypeName, yup.AnySchema> = {
  ActivatingEventRecorded: activatingEventRecordedPayloadSchema,
  BehaviorsRecorded: behaviorsRecordedPayloadSchema,
  AutomaticThoughtRecorded: automaticThoughtRecordedPayloadSchema,
  EvidenceForRecorded: evidenceForRecordedPayloadSchema,
  EvidenceAgainstRecorded: evidenceAgainstRecordedPayloadSchema,
  CognitiveDistortionsIdentified: cognitiveDistortionsIdentifiedPayloadSchema,
  AlternativeThoughtRecorded: alternativeThoughtRecordedPayloadSchema,
  OutcomeRated: outcomeRatedPayloadSchema,
  LocationReminderSet: locationReminderSetPayloadSchema,
};

/**
 * A UUID of ANY version — deliberately not `yup.string().uuid()`.
 *
 * yup@0.32's `uuid()` regex accepts versions 1–5 only (`[1-5]` in its version nibble), and every
 * `eventId` this transport carries is a **UUIDv7**: `@cc/es-core`'s `uuidv7()` is what the outbox
 * mints, because a v7 id sorts by its own timestamp and that is what makes an originator-minted
 * `_key` a usable idempotency handle. So `uuid()` rejected every batch the mobile outbox would ever
 * send — `events[0].eventId must be a valid UUID` — and nothing caught it because the transport has
 * had no live caller yet (the mobile half is plan 00 D13). Found by driving the wizard through the
 * shipped resolver on dev, 2026-09-03.
 *
 * This is not a loosened check: it still requires the canonical 8-4-4-4-12 hex shape and an
 * RFC-4122/9562 variant nibble; it merely stops asserting a version the standard has since added.
 */
const UUID_ANY_VERSION =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** One wire event of the batch (03 §2.4 shape); `payload` is validated by its `type`. */
export const thoughtTestEventInputSchema = yup.object({
  eventId: yup
    .string()
    .matches(UUID_ANY_VERSION, 'eventId must be a UUID')
    .required(),
  type: yup
    .mixed<ThoughtTestEventTypeName>()
    .oneOf([...THOUGHT_TEST_EVENT_TYPES])
    .required(),
  occurredAt: yup.date().required(),
  clientSeq: yup.number().integer().min(0).required(),
  payload: yup.lazy((_value, options) => {
    const type = (
      options.parent as { type?: ThoughtTestEventTypeName } | undefined
    )?.type;
    return (type && payloadSchemaByType[type]) ?? yup.mixed().required();
  }),
});

export type ThoughtTestEventInputValidated = yup.Asserts<
  typeof thoughtTestEventInputSchema
>;

/** The mutation arguments — the shield's inputRule schema. */
export const submitThoughtTestEventsArgsSchema = yup.object({
  thoughtTestId: yup.string().required(),
  events: yup
    .array()
    .of(thoughtTestEventInputSchema.required())
    .min(1)
    .required(),
});

// ── E01 · CreateStandaloneCard (online command; the legacy editor's fields, both ends) ──────────
// (`basicThoughtSchema` is declared once, above, and shared with the wizard's
//  AlternativeThoughtRecorded codec — the two always described the same board type.)

/**
 * `thoughtTestId` is minted by the ORIGINATOR (`thoughtTests/<uuid>`, offline-capable) — it is the
 * DCB boundary and the legacy `thoughts/<key>` of the card's back-projection.
 */
export const createStandaloneCardInputSchema = yup.object({
  thoughtTestId: yup
    .string()
    .trim()
    .matches(/^thoughtTests\/[A-Za-z0-9_-]{1,64}$/)
    .required(),
  cardName: yup.string().trim().nullable(),
  automaticThought: yup.string().trim().required(),
  realisticThoughts: yup
    .array()
    .of(basicThoughtSchema.required())
    .min(1)
    .required(),
});

export type CreateStandaloneCardInputValidated = yup.Asserts<
  typeof createStandaloneCardInputSchema
>;

/** The mutation arguments — the shield's inputRule schema. */
export const createStandaloneCardArgsSchema = yup.object({
  input: createStandaloneCardInputSchema.required(),
});

// ── E01 · CreatePortalCard — the same card authored ON BEHALF of a client (portal) ─────────────

/**
 * The ES half of the therapist's "Create a Coping Card for a Client" form. ONE operation serves
 * both eras (the wire contract is identical — founder 2026-08-31: the name follows the contract,
 * not the era), so this schema is NOT a shield inputRule: it would also gate the legacy branch,
 * which has always accepted a looser shape. The ES branch runs it in-resolver, as the both-ends
 * codec it is. `cardName` is absent (the portal form has none) and the card's time reminders stay
 * on the legacy write — reminders (E05) are not a ported domain.
 */
export const createPortalCardInputSchema = yup.object({
  clientId: yup
    .string()
    .trim()
    .matches(/^users\/[A-Za-z0-9_-]{1,64}$/)
    .required(),
  thoughtTestId: yup
    .string()
    .trim()
    .matches(/^thoughtTests\/[A-Za-z0-9_-]{1,64}$/)
    .required(),
  automaticThought: yup.string().trim().required(),
  realisticThoughts: yup
    .array()
    .of(basicThoughtSchema.required())
    .min(1)
    .required(),
});

export type CreatePortalCardInputValidated = yup.Asserts<
  typeof createPortalCardInputSchema
>;

export const thoughtTestingValidators = {
  activatingEventRecordedPayloadSchema,
  thoughtTestEventInputSchema,
  submitThoughtTestEventsArgsSchema,
  createStandaloneCardInputSchema,
  createStandaloneCardArgsSchema,
  createPortalCardInputSchema,
};
